'use client';

import { PropsWithChildren, ReactNode, createContext, useMemo, useState } from 'react';

import classNames from 'classnames';
import Link from 'next/link';

import { MT5OfferingModal } from '@/blocks/components/MT5OfferingModal/MT5OfferingModal';
import { LinkSignup } from '@/blocks/components/PlatformLink';
import { WithClassName } from '@/blocks/components/types';
import { MT5_VIA_PXBT } from '@/global/public-settings';
import { createContextHook } from '@/hooks/createContextHook';
import { useAnalytics } from '@/providers/analytics/WithAnalytics';

type Context = {
  open: boolean;
  handleOpen(content: string): void;
  handleClose(): void;
};

const MT5OfferingModalContext = createContext<Context | undefined>(undefined);

export function WithMT5Modal(props: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [utmContent, setUtmContent] = useState<string>('');

  const contextValue = useMemo(
    () => ({
      open,
      handleOpen: (content: string) => {
        setUtmContent(content);
        setOpen(true);
      },
      handleClose: () => {
        setOpen(false);
      },
    }),
    [open],
  );

  return (
    <MT5OfferingModalContext.Provider value={contextValue}>
      {props.children}
      <MT5OfferingModal utmContent={utmContent} />
    </MT5OfferingModalContext.Provider>
  );
}

export const useMT5ModalContext = createContextHook(MT5OfferingModalContext, 'MT5OfferingModalProvider');

type MT5EventLabel =
  | 'download'
  | 'open_account'
  | 'demo_account'
  | 'access_android'
  | 'access_ios'
  | 'webTrader'
  | 'platform_icons'
  | 'faq'
  | 'start_trading'
  | 'get_started';

function MT5PXBModalTButton({
  children,
  className,
  eventLabel,
  blockName,
}: {
  eventLabel: MT5EventLabel;
  blockName: string;
} & PropsWithChildren<WithClassName>) {
  const { handleOpen } = useMT5ModalContext();
  const { track } = useAnalytics();
  return (
    <button
      className={classNames(className)}
      onClick={() => {
        handleOpen(eventLabel);
        track({
          event_name: `site_cta_buttons_mt_5_${eventLabel}_click`,
          event_label: eventLabel,
          block_name: blockName,
        });
      }}
    >
      {children}
    </button>
  );
}

export type MT5Platform = 'windows' | 'mac' | 'android' | 'ios' | 'web';

export function MT5Link({
  children,
  className,
  eventLabel,
  blockName,
  platform,
}: {
  eventLabel: MT5EventLabel;
  blockName: string;
  platform: MT5Platform;
} & PropsWithChildren<WithClassName>) {
  const { track } = useAnalytics();

  const mt5DownloadLink = {
    windows: 'https://download.mql5.com/cdn/web/pxbt.trading.ltd/mt5/pxbttrading5setup.exe',
    mac: 'https://download.mql5.com/cdn/web/metaquotes.software.corp/mt5/MetaTrader5.pkg.zip',
    android: 'https://download.mql5.com/cdn/mobile/mt5/android?server=PXBTTrading-Live',
    ios: 'https://download.mql5.com/cdn/mobile/mt5/ios?server=PXBTTrading-Live',
  };

  if (MT5_VIA_PXBT) {
    return (
      <MT5PXBModalTButton blockName={blockName} eventLabel={eventLabel} className={className}>
        {children}
      </MT5PXBModalTButton>
    );
  }

  if (!platform || platform === 'web') {
    return (
      <LinkSignup
        className={classNames(className)}
        onClick={() => {
          track({
            event_name: `site_cta_buttons_mt_5_${eventLabel}_click`,
            event_label: eventLabel,
            block_name: blockName,
          });
        }}
      >
        {children}
      </LinkSignup>
    );
  }

  return (
    <Link
      href={mt5DownloadLink[platform]}
      target={'_blank'}
      className={classNames(className)}
      onClick={() => {
        track({
          event_name: `site_cta_buttons_mt_5_${eventLabel}_click`,
          event_label: eventLabel,
          block_name: blockName,
        });
      }}
    >
      {children}
    </Link>
  );
}
